import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Download,
  Upload,
  Search,
  Filter,
  MoreVertical,
  Target,
  CheckCircle,
  Clock,
  AlertTriangle,
  DollarSign,
  Calendar,
  Users,
  FileText,
  BarChart3,
  TrendingUp,
  Shield,
  Zap,
  Code as CodeIcon,
  Bell as BellIcon,
  BellRing as BellRingIcon,
  Activity,
  Layers,
  GitBranch,
  Settings,
  Play,
  Pause,
  Square,
  RefreshCw
} from 'lucide-react';

const EnhancedFixedPriceContract = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  });

  // Enhanced mock data for Fixed-Price contracts
  const mockContracts = [
    {
      id: 'FP-001',
      name: 'E-commerce Platform Development',
      client: 'TechCorp Solutions',
      status: 'active',
      totalValue: 150000,
      currency: 'USD',
      startDate: '2024-01-15',
      endDate: '2024-06-15',
      progress: 65,
      scope: [
        { id: 1, item: 'Frontend development with React', status: 'completed', percentage: 100, assignedTo: 'John Doe', dueDate: '2024-02-15' },
        { id: 2, item: 'Backend API development', status: 'in-progress', percentage: 75, assignedTo: 'Jane Smith', dueDate: '2024-03-15' },
        { id: 3, item: 'Database design and implementation', status: 'completed', percentage: 100, assignedTo: 'Mike Johnson', dueDate: '2024-02-28' },
        { id: 4, item: 'Payment gateway integration', status: 'in-progress', percentage: 50, assignedTo: 'Sarah Wilson', dueDate: '2024-04-15' },
        { id: 5, item: 'Admin dashboard development', status: 'pending', percentage: 0, assignedTo: 'Tom Brown', dueDate: '2024-05-15' }
      ],
      deliverables: [
        { id: 1, name: 'Source code repository', status: 'completed', dueDate: '2024-02-15', deliveredDate: '2024-02-10', qualityScore: 95 },
        { id: 2, name: 'Deployed application', status: 'in-progress', dueDate: '2024-05-15', deliveredDate: null, qualityScore: null },
        { id: 3, name: 'Technical documentation', status: 'pending', dueDate: '2024-06-01', deliveredDate: null, qualityScore: null },
        { id: 4, name: 'User manual', status: 'pending', dueDate: '2024-06-10', deliveredDate: null, qualityScore: null },
        { id: 5, name: 'Training materials', status: 'pending', dueDate: '2024-06-15', deliveredDate: null, qualityScore: null }
      ],
      milestones: [
        { 
          id: 1,
          name: 'Project Kickoff', 
          date: '2024-01-15', 
          status: 'completed', 
          value: 15000,
          percentage: 10,
          description: 'Project initiation and team setup',
          deliverables: ['Project charter', 'Team assignments', 'Initial requirements'],
          actualDate: '2024-01-15',
          variance: 0
        },
        { 
          id: 2,
          name: 'Design Phase Complete', 
          date: '2024-02-15', 
          status: 'completed', 
          value: 30000,
          percentage: 20,
          description: 'UI/UX design and architecture planning',
          deliverables: ['Wireframes', 'Design system', 'Technical architecture'],
          actualDate: '2024-02-12',
          variance: -3
        },
        { 
          id: 3,
          name: 'Development Phase 1', 
          date: '2024-03-15', 
          status: 'completed', 
          value: 45000,
          percentage: 30,
          description: 'Core functionality development',
          deliverables: ['User authentication', 'Product catalog', 'Shopping cart'],
          actualDate: '2024-03-18',
          variance: 3
        },
        { 
          id: 4,
          name: 'Development Phase 2', 
          date: '2024-04-15', 
          status: 'in-progress', 
          value: 45000,
          percentage: 30,
          description: 'Advanced features and integrations',
          deliverables: ['Payment processing', 'Order management', 'Admin panel'],
          actualDate: null,
          variance: null
        },
        { 
          id: 5,
          name: 'Testing & QA', 
          date: '2024-05-15', 
          status: 'pending', 
          value: 30000,
          percentage: 20,
          description: 'Quality assurance and testing',
          deliverables: ['Test reports', 'Bug fixes', 'Performance optimization'],
          actualDate: null,
          variance: null
        },
        { 
          id: 6,
          name: 'Final Delivery', 
          date: '2024-06-15', 
          status: 'pending', 
          value: 15000,
          percentage: 10,
          description: 'Final deployment and handover',
          deliverables: ['Production deployment', 'Documentation', 'Training sessions'],
          actualDate: null,
          variance: null
        }
      ],
      changeRequests: [
        {
          id: 1,
          title: 'Additional payment gateway integration',
          description: 'Client requested integration with PayPal in addition to Stripe',
          status: 'approved',
          impact: 'positive',
          costChange: 5000,
          timeChange: 7,
          submittedDate: '2024-02-20',
          approvedDate: '2024-02-22',
          approvedBy: 'Project Manager',
          priority: 'medium'
        },
        {
          id: 2,
          title: 'Mobile app requirement change',
          description: 'Client decided to include mobile app development',
          status: 'pending',
          impact: 'positive',
          costChange: 25000,
          timeChange: 30,
          submittedDate: '2024-03-10',
          approvedDate: null,
          approvedBy: null,
          priority: 'high'
        }
      ],
      qualityGates: [
        {
          id: 1,
          name: 'Code Review Gate',
          status: 'passed',
          date: '2024-03-10',
          criteria: ['Code quality', 'Security review', 'Performance check'],
          score: 95,
          reviewer: 'Senior Developer',
          comments: 'Code quality meets standards'
        },
        {
          id: 2,
          name: 'Security Audit Gate',
          status: 'pending',
          date: '2024-04-20',
          criteria: ['Security scan', 'Penetration testing', 'Compliance check'],
          score: null,
          reviewer: null,
          comments: null
        }
      ],
      performanceMetrics: {
        qualityScore: 92,
        timelinessScore: 88,
        customerSatisfaction: 95,
        budgetVariance: -2.5,
        scheduleVariance: 5,
        scopeVariance: 0,
        teamProductivity: 85,
        clientEngagement: 90
      },
      riskLevel: 'low',
      risks: [
        {
          id: 1,
          title: 'Third-party API dependencies',
          level: 'medium',
          impact: 'schedule',
          probability: 30,
          mitigation: 'Alternative API options identified',
          owner: 'Technical Lead',
          status: 'monitoring'
        }
      ],
      teamMembers: [
        { id: 1, name: 'John Doe', role: 'Frontend Developer', allocation: 100, hourlyRate: 75 },
        { id: 2, name: 'Jane Smith', role: 'Backend Developer', allocation: 100, hourlyRate: 80 },
        { id: 3, name: 'Mike Johnson', role: 'Database Administrator', allocation: 50, hourlyRate: 70 },
        { id: 4, name: 'Sarah Wilson', role: 'Integration Specialist', allocation: 75, hourlyRate: 85 },
        { id: 5, name: 'Tom Brown', role: 'UI/UX Designer', allocation: 100, hourlyRate: 65 }
      ]
    }
  ];

  // Load contracts
  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    setLoading(true);
    try {
      // Mock API call
      setContracts(mockContracts);
      setPagination(prev => ({ ...prev, total: mockContracts.length }));
    } catch (error) {
      console.error('Error loading contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContract = async (contractId) => {
    setIsDeleting(true);
    try {
      // Mock API call
      setContracts(prev => prev.filter(contract => contract.id !== contractId));
      setShowDeleteModal(false);
      setContractToDelete(null);
    } catch (error) {
      console.error('Error deleting contract:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in-progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Contracts</p>
              <p className="text-2xl font-bold text-gray-900">{contracts.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Contracts</p>
              <p className="text-2xl font-bold text-gray-900">
                {contracts.filter(c => c.status === 'active').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">
                ${contracts.reduce((sum, c) => sum + c.totalValue, 0).toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Progress</p>
              <p className="text-2xl font-bold text-gray-900">
                {Math.round(contracts.reduce((sum, c) => sum + c.progress, 0) / contracts.length)}%
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <TrendingUp className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Contracts List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Fixed-Price Contracts</h3>
            <div className="flex items-center gap-3">
              <button className="btn-modern-outline">
                <Download size={16} />
                Export
              </button>
              <button className="btn-modern-primary">
                <Plus size={16} />
                New Contract
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contract
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{contract.name}</div>
                      <div className="text-sm text-gray-500">ID: {contract.id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{contract.client}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${contract.totalValue.toLocaleString()} {contract.currency}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${contract.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">{contract.progress}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                      {contract.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRiskLevelColor(contract.riskLevel)}`}>
                      {contract.riskLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button className="text-blue-600 hover:text-blue-900">
                        <Eye size={16} />
                      </button>
                      <button className="text-green-600 hover:text-green-900">
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900"
                        onClick={() => {
                          setContractToDelete(contract);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderScopeTracking = () => (
    <div className="space-y-6">
      {contracts.map((contract) => (
        <div key={contract.id} className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{contract.name}</h3>
            <p className="text-sm text-gray-600">Client: {contract.client}</p>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {contract.scope.map((item) => (
                <div key={item.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{item.item}</h4>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Assigned to:</span> {item.assignedTo}
                    </div>
                    <div>
                      <span className="font-medium">Due date:</span> {item.dueDate}
                    </div>
                    <div>
                      <span className="font-medium">Progress:</span> {item.percentage}%
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderMilestoneTracking = () => (
    <div className="space-y-6">
      {contracts.map((contract) => (
        <div key={contract.id} className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{contract.name}</h3>
            <p className="text-sm text-gray-600">Client: {contract.client}</p>
          </div>
          
          <div className="p-6">
            <div className="space-y-6">
              {contract.milestones.map((milestone, index) => (
                <div key={milestone.id} className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      milestone.status === 'completed' ? 'bg-green-100 text-green-600' :
                      milestone.status === 'in-progress' ? 'bg-blue-100 text-blue-600' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {milestone.status === 'completed' ? <CheckCircle size={16} /> :
                       milestone.status === 'in-progress' ? <Clock size={16} /> :
                       <Target size={16} />}
                    </div>
                    {index < contract.milestones.length - 1 && (
                      <div className="w-px h-8 bg-gray-200 ml-4"></div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-medium text-gray-900">{milestone.name}</h4>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm font-medium text-gray-900">
                          ${milestone.value.toLocaleString()}
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(milestone.status)}`}>
                          {milestone.status}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-1">{milestone.description}</p>
                    
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Planned Date:</p>
                        <p className="text-sm text-gray-600">{milestone.date}</p>
                      </div>
                      {milestone.actualDate && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Actual Date:</p>
                          <p className="text-sm text-gray-600">{milestone.actualDate}</p>
                        </div>
                      )}
                      {milestone.variance !== null && (
                        <div>
                          <p className="text-sm font-medium text-gray-700">Variance:</p>
                          <p className={`text-sm ${milestone.variance > 0 ? 'text-red-600' : milestone.variance < 0 ? 'text-green-600' : 'text-gray-600'}`}>
                            {milestone.variance > 0 ? '+' : ''}{milestone.variance} days
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Deliverables:</p>
                      <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                        {milestone.deliverables.map((deliverable, idx) => (
                          <li key={idx}>{deliverable}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderChangeRequests = () => (
    <div className="space-y-6">
      {contracts.map((contract) => (
        <div key={contract.id} className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{contract.name}</h3>
            <p className="text-sm text-gray-600">Client: {contract.client}</p>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {contract.changeRequests.map((request) => (
                <div key={request.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{request.title}</h4>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        request.priority === 'high' ? 'text-red-600 bg-red-100' :
                        request.priority === 'medium' ? 'text-yellow-600 bg-yellow-100' :
                        'text-green-600 bg-green-100'
                      }`}>
                        {request.priority}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{request.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Cost Impact:</span>
                      <span className={`ml-2 ${request.costChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {request.costChange > 0 ? '+' : ''}${request.costChange.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Time Impact:</span>
                      <span className={`ml-2 ${request.timeChange > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {request.timeChange > 0 ? '+' : ''}{request.timeChange} days
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Submitted:</span>
                      <span className="ml-2 text-gray-600">{request.submittedDate}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Approved:</span>
                      <span className="ml-2 text-gray-600">{request.approvedDate || 'Pending'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPerformanceMetrics = () => (
    <div className="space-y-6">
      {contracts.map((contract) => (
        <div key={contract.id} className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{contract.name}</h3>
            <p className="text-sm text-gray-600">Client: {contract.client}</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {contract.performanceMetrics.qualityScore}%
                </div>
                <div className="text-sm text-gray-600">Quality Score</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {contract.performanceMetrics.timelinessScore}%
                </div>
                <div className="text-sm text-gray-600">Timeliness Score</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {contract.performanceMetrics.customerSatisfaction}%
                </div>
                <div className="text-sm text-gray-600">Customer Satisfaction</div>
              </div>
              
              <div className="text-center">
                <div className={`text-3xl font-bold mb-2 ${
                  contract.performanceMetrics.budgetVariance < 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {contract.performanceMetrics.budgetVariance > 0 ? '+' : ''}{contract.performanceMetrics.budgetVariance}%
                </div>
                <div className="text-sm text-gray-600">Budget Variance</div>
              </div>
              
              <div className="text-center">
                <div className={`text-3xl font-bold mb-2 ${
                  contract.performanceMetrics.scheduleVariance < 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {contract.performanceMetrics.scheduleVariance > 0 ? '+' : ''}{contract.performanceMetrics.scheduleVariance}%
                </div>
                <div className="text-sm text-gray-600">Schedule Variance</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 mb-2">
                  {contract.performanceMetrics.teamProductivity}%
                </div>
                <div className="text-sm text-gray-600">Team Productivity</div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'scope':
        return renderScopeTracking();
      case 'milestones':
        return renderMilestoneTracking();
      case 'changes':
        return renderChangeRequests();
      case 'performance':
        return renderPerformanceMetrics();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/contracts')}
            className="btn-modern-secondary"
          >
            <ArrowLeft size={16} />
            Back to Contracts
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Fixed-Price Contracts</h1>
            <p className="page-subtitle">
              Manage fixed-price contracts with comprehensive scope, milestone, and performance tracking
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="btn-modern-outline">
              <Download size={16} />
              Export Report
            </button>
            <button className="btn-modern-primary">
              <Plus size={16} />
              New Contract
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          <button
            className={`page-tabs-item ${activeTab === 'overview' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BarChart3 size={16} />
            Overview
          </button>
          <button
            className={`page-tabs-item ${activeTab === 'scope' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('scope')}
          >
            <Target size={16} />
            Scope Tracking
          </button>
          <button
            className={`page-tabs-item ${activeTab === 'milestones' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('milestones')}
          >
            <CheckCircle size={16} />
            Milestones
          </button>
          <button
            className={`page-tabs-item ${activeTab === 'changes' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('changes')}
          >
            <GitBranch size={16} />
            Change Requests
          </button>
          <button
            className={`page-tabs-item ${activeTab === 'performance' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            <TrendingUp size={16} />
            Performance
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="page-tab-content">
        {renderTabContent()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Contract</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete "{contractToDelete?.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center gap-3 mt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn-modern-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteContract(contractToDelete?.id)}
                  disabled={isDeleting}
                  className="btn-modern-danger"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedFixedPriceContract;
